import React, { useEffect, useRef, useState } from 'react'
import Modal from '@/v2/components/common/Modal'
import useModals from '@/v2/hooks/useModals'
import RadioField from '@/v2/components/common/RadioField'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { subWeeks, parseISO, format, isValid } from 'date-fns'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Label from '@/v2/components/common/FieldLabel'
import { useGetDoctors } from '@/v2/hooks/useDoctors'
import SelectField from '@/v2/components/common/SelectField'

const dateInit = subWeeks(new Date(), 2)
const dateMax = new Date()
const formattedInitDate = format(dateInit, 'yyyy-MM-dd')
const formattedMaxDate = format(dateMax, 'yyyy-MM-dd')

const schema = yup
  .object({
    doctor: yup
      .string()
      .min(1, 'Please select a doctor')
      .required('Please select a doctor'),
    start_date: yup
      .date()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string' && originalValue.trim() !== '') {
          const parsed = parseISO(originalValue)
          return isValid(parsed) ? parsed : new Date('')
        }
        return value
      })
      .required('Start date is required')
      .max(dateMax, 'Start date must be less than today'),
    end_date: yup
      .date()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string' && originalValue.trim() !== '') {
          const parsed = parseISO(originalValue)
          return isValid(parsed) ? parsed : new Date('')
        }
        return value
      })
      .required('End date is required')
      .max(dateMax, 'End date must be less than today'),
    type: yup
      .mixed()
      .oneOf(['print'], 'Type must be print')
      .required('Type is required'),
  })
  .test(
    'start-less-than-end',
    'Start date must be less than end date',
    function(value) {
      const { start_date, end_date } = value
      if (start_date && end_date) {
        return start_date <= end_date
      }
      return true
    }
  )

const Receipts = ({ isOpen, onClose, patient }) => {
  const modals = useModals()
  const dateStartRef = useRef(null)
  const dateEndRef = useRef(null)
  const { data: doctors } = useGetDoctors()
  const [doctorsOptions, setDoctorsOptions] = useState([])

  useEffect(() => {
    if (doctors) {
      const optionsMap = doctors.map(doctor => ({
        ...doctor,
        value: doctor.id,
        label: doctor.fullName,
      }))
      setDoctorsOptions(optionsMap)
    }
  }, [doctors])

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      doctor: '',
      start_date: formattedInitDate,
      end_date: formattedMaxDate,
      type: 'print',
    },
  })

  const { ref: startDateInputRef, ...startDateRest } = register('start_date')
  const { ref: endDateInputRef, ...endDateRest } = register('end_date')
  const watchStartDate = watch('start_date')
  const watchEndDate = watch('end_date')

  useEffect(() => {
    const startInput = dateStartRef.current
    const endInput = dateEndRef.current

    if (watchStartDate && endInput) {
      endInput.min = watchStartDate
      endInput.ariaValueMin = watchStartDate
    }
    if (watchEndDate && startInput) {
      startInput.max = watchEndDate
      startInput.ariaValueMax = watchEndDate
    }
  }, [watchStartDate, watchEndDate])

  const onSubmit = data => {
    const receiptURL = `/v2/billing/patients/${patient.id}/receipts`
    const params = Object.entries(data)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    const receipt = window.open(
      [receiptURL, params].join('?'),
      'noopener,noreferrer'
    )

    if (receipt) {
      modals.close()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      title="HSA/FSA Receipt"
      confirmLabel="Create Receipt"
      maxWidth="xs"
      fullWidth
    >
      <FormControl
        component="form"
        variant="filled"
        sx={{
          '.MuiRadio-root': {
            padding: 1,
          },
        }}
      >
        <FormControl variant="standard">
          <Label htmlFor="receipt-start-date">Start date</Label>
          <TextField
            id="receipt-start-date"
            fullWidth
            error={!!errors.start_date}
            helperText={errors.start_date?.message}
            type="date"
            inputRef={e => {
              startDateInputRef(e)
              dateStartRef.current = e
            }}
            name="start_date"
            {...startDateRest}
          />
        </FormControl>
        <FormControl variant="standard">
          <Label htmlFor="receipt-end-date">End date</Label>
          <TextField
            id="receipt-end-date"
            fullWidth
            error={!!errors.end_date}
            helperText={errors.end_date?.message}
            type="date"
            name="end_date"
            inputRef={e => {
              endDateInputRef(e)
              dateEndRef.current = e
            }}
            {...endDateRest}
          />
        </FormControl>
        <Controller
          name="doctor"
          control={control}
          render={({ field }) => (
            <FormControl variant="standard">
              <SelectField
                fullWidth
                label="Provider"
                placeholder="Choose a provider"
                options={doctorsOptions}
                value={field.value}
                onChange={field.onChange}
                error={errors?.doctor?.message}
              />
            </FormControl>
          )}
        />
        <RadioField
          name="type"
          color="secondary"
          id="receipt-types"
          defaultValue="print"
          sx={{ mt: -4 }}
          options={[{ label: 'Print', value: 'print' }]}
          {...register('type')}
        />
      </FormControl>
    </Modal>
  )
}

export default Receipts
